import React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../api/auth";
import logo from "../../images/LogoNavbar1.svg";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function ConfirmEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialEmail =
    location.state?.email ||
    searchParams.get("email") ||
    "";

  const token = searchParams.get("token") || "";

  const [email, setEmail] = React.useState(initialEmail);
  const [code, setCode] = React.useState(searchParams.get("code") || "");
  const [message, setMessage] = React.useState(location.state?.message || "");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(Boolean(token && initialEmail));
  const [resending, setResending] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);

  const confirm = React.useCallback(async ({ nextEmail, nextCode, nextToken }) => {
    const payload = {
      email: String(nextEmail || "").trim().toLowerCase(),
      code: String(nextCode || "").trim(),
      token: String(nextToken || "").trim(),
    };

    if (!payload.email || (!payload.code && !payload.token)) {
      setError("Ingresá tu email y el código de verificación.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await authApi.confirmEmail(payload);
      setConfirmed(true);
      setMessage(data?.message || "Email confirmado. Tu acceso queda pendiente de aprobación.");
    } catch (err) {
      setError(err.message || "No se pudo confirmar el email.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (token && initialEmail) {
      confirm({ nextEmail: initialEmail, nextToken: token });
    }
  }, [token, initialEmail, confirm]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    await confirm({ nextEmail: email, nextCode: code });
  };

  const handleResend = async () => {
    try {
      setResending(true);
      setError("");
      const cleanEmail = String(email || "").trim().toLowerCase();
      if (!cleanEmail) {
        setError("Ingresá tu email para reenviar el código.");
        return;
      }
      const data = await authApi.resendConfirmation({ email: cleanEmail });
      setMessage(data?.message || "Si el email existe, se envió un nuevo código.");
    } catch (err) {
      setError(err.message || "No se pudo reenviar el código.");
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F6F7FB", display: "grid", placeItems: "center", px: 2 }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box component="img" src={logo} alt="WebProfit" sx={{ width: 64, height: 64, objectFit: "contain" }} />
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ width: 52, height: 52, mx: "auto", mb: 1.5, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                <MarkEmailReadRoundedIcon />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Confirmar email
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
                Te enviamos un código. Confirmá tu correo y después WebProfit aprueba el acceso al panel.
              </Typography>
            </Box>
          </Stack>

          {message && <Alert severity={confirmed ? "success" : "info"} sx={{ mb: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                fullWidth
                disabled={confirmed}
              />
              <TextField
                label="Código de verificación"
                name="code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                required={!token}
                fullWidth
                disabled={confirmed}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                helperText="Revisá tu correo. El código tiene 6 números."
              />

              {!confirmed ? (
                <Button type="submit" disabled={loading} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.25 }}>
                  {loading ? "Confirmando..." : "Confirmar email"}
                </Button>
              ) : (
                <Button onClick={() => navigate("/login", { replace: true })} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.25 }}>
                  Ir a login
                </Button>
              )}

              <Button type="button" onClick={handleResend} disabled={resending || confirmed} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                {resending ? "Reenviando..." : "Reenviar código"}
              </Button>
            </Stack>
          </Box>

          <Typography sx={{ textAlign: "center", mt: 3, color: "rgba(25,36,47,0.7)" }}>
            ¿Ya confirmaste y fuiste aprobado?{" "}
            <Link component={RouterLink} to="/login" sx={{ color: BRAND.orange, fontWeight: 900 }}>
              Ingresar
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
